/**
 * Modified for HDMX Super sku
 */

import { Swatch } from '@/components/blocks/Swatch';
import { attributeSx, AttributeSx } from '@/components/content/ProductDetails/styles/attributes';
import { SWATCH } from '@/data/constants/catalog';
import { ProductAttribute, Selection } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { getAttributeType } from '@/utils/productAttributes';
import { Box, Stack, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import { FC } from 'react';

type ProductAttributeV2Props = {
	attribute: ProductAttribute;
	skusAsAttrsList: Record<string, string>[];
	onAttributeChange: (attrId: string, valueId: string) => void;
	selection: Selection;
	isPivot?: boolean; // the pivot attribute can never be disabled, i.e., the selection alternator
	availability?: ProductAvailabilityData[];
	forceDisabled?: boolean;
	isHiddenByBase?: boolean;
};
export const ProductAttributeV2: FC<ProductAttributeV2Props> = ({
	attribute,
	skusAsAttrsList,
	onAttributeChange,
	selection,
	isPivot = false,
	forceDisabled = false,
	isHiddenByBase,
}) => {
	const { name, values, identifier: attrIdentifier } = attribute;
	const { attrsByIdentifier: currentlySelected = {} } = selection;
	const isSwatch = SWATCH === getAttributeType(values);
	const valueId = isSwatch ? currentlySelected[attrIdentifier] : null;
	const swatchValue = isSwatch ? values.find(({ identifier: id }) => id === valueId)?.value : null;

	return (
		<Stack sx={isHiddenByBase ? attributeSx.hidden : attributeSx.container}>
			<Typography variant="bodyBaseline" sx={attributeSx.label}>
				{name}:&nbsp;
				<Typography variant="bodyBaseline" sx={attributeSx.value}>
					{isSwatch ? swatchValue : currentlySelected[attrIdentifier]}
				</Typography>
			</Typography>
			<Stack sx={attributeSx.optionsContainer}>
				{values.map(({ identifier, value, image1path }) => {
					// this value is selected if the current SKU has its identifier as the value of the
					//   attribute's identifier
					const selected = currentlySelected[attrIdentifier] === identifier;
					// if this isn't part of the current sku's selection, check if such a SKU exists by
					//   replacing the current selection's value for the attribute
					const asSku = selected ? null : { ...currentlySelected, [attrIdentifier]: identifier };
					// mark this value disabled if selecting it would yield no SKU -- but only if not pivot
					const disabled =
						(!selected && !skusAsAttrsList.find((sku) => isEqual(sku, asSku)) && !isPivot) ||
						forceDisabled;

					return isSwatch ? (
						<Swatch
							key={identifier}
							title={value}
							onClick={() => onAttributeChange(attrIdentifier, identifier)}
							selected={selected}
							image={image1path}
							data-testid={`swatch-attribute-${identifier.toLowerCase()}`}
							id={`swatch-attribute-${identifier.toLowerCase()}`}
						/>
					) : (
						<Box
							sx={AttributeSx(disabled, selected)}
							onClick={() => onAttributeChange(attrIdentifier, identifier)}
							data-testid={`attribute-${identifier.toLowerCase()}`}
							id={`attribute-${identifier.toLowerCase()}`}
						>
							<Typography variant="bodyBaseline" sx={attributeSx.text}>
								{value}
							</Typography>
						</Box>
					);
				})}
			</Stack>
		</Stack>
	);
};
